import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useQuery } from '../lib/useQuery.js';
import { Page } from '../app/AppShell.js';
import { StatTile, BarSeries } from '../components/charts.js';
import { Loading, ErrorNote } from '../components/ui.js';

function last14Days(): string[] {
  return Array.from({ length: 14 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (13 - i));
    return d.toISOString().slice(0, 10);
  });
}

export function DashboardScreen(): ReactNode {
  const summary = useQuery(() => api.reportSummary(), [], { pollMs: 30000 });
  const from = last14Days()[0];
  const plays = useQuery(() => api.proofOfPlay({ groupBy: 'day', from }), [from]);

  const byDay = new Map((plays.data?.rows ?? []).map((r) => [r.key, r.plays]));
  const series = last14Days().map((date) => ({
    label: date.slice(5),
    value: byDay.get(date) ?? 0,
  }));

  const s = summary.data;

  return (
    <Page title="Dashboard">
      {summary.loading && !s ? <Loading what="dashboard" /> : null}
      {summary.error ? <ErrorNote error={summary.error} /> : null}

      {s ? (
        <div className="stack" style={{ gap: 20 }}>
          <div className="tile-row">
            <StatTile
              label="Displays online"
              value={`${s.displays.online} / ${s.displays.total}`}
              tone={s.displays.total > 0 && s.displays.online < s.displays.total ? 'warn' : 'good'}
            />
            <StatTile label="Plays today" value={s.playsToday.toLocaleString()} />
            <StatTile
              label="Active alerts"
              value={s.activeAlerts}
              tone={s.activeAlerts > 0 ? 'bad' : 'neutral'}
              hint={s.activeAlerts > 0 ? undefined : 'all clear'}
            />
            <StatTile label="Published layouts" value={s.layoutsPublished} />
            <StatTile label="Media assets" value={s.mediaCount} />
          </div>

          <div className="card card-pad">
            <div className="row between" style={{ marginBottom: 8 }}>
              <strong className="small">Plays · last 14 days</strong>
              <Link className="small" to="/reports">
                Reports →
              </Link>
            </div>
            {plays.error ? <ErrorNote error={plays.error} /> : <BarSeries data={series} />}
          </div>

          {s.topFaults.length > 0 ? (
            <div className="card card-pad">
              <strong className="small">Top faults</strong>
              <ul className="small muted" style={{ margin: '6px 0 0' }}>
                {s.topFaults.map((f) => (
                  <li key={f.code}>
                    {f.code} — {f.count}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}
    </Page>
  );
}
